import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const IconBrands = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const IconBranches = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const IconCal = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

// Role configs
const ROLE = {
  admin: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', avatarBg: '#2563EB', label: 'Admin', accentBorder: '#3B82F6' },
  owner: { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', avatarBg: '#16A34A', label: 'Owner', accentBorder: '#22C55E' },
};

function UserCard({ u, onEdit, onDelete, onGoBrands, onGoBranches, onAddBrand, onAddBranch }) {
  const [hovered, setHovered] = useState(false);
  const role = ROLE[u.role] || ROLE.owner;
  const initials = u.username?.slice(0, 2).toUpperCase();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderTop: `1.5px solid ${hovered ? role.accentBorder : '#EBEBEB'}`,
        borderRight: `1.5px solid ${hovered ? role.accentBorder : '#EBEBEB'}`,
        borderBottom: `1.5px solid ${hovered ? role.accentBorder : '#EBEBEB'}`,
        borderLeft: `5px solid ${role.accentBorder}`,
        borderRadius: 16,
        boxShadow: hovered ? '0 6px 24px rgba(0,0,0,0.10)' : '0 1px 6px rgba(0,0,0,0.06)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <div style={{ padding: '11px 14px 10px', display: 'flex', gap: 12, alignItems: 'center' }}>
        {/* Avatar — brand logo if available, else initials */}
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: u.brand_logo ? '#f5f5f5' : role.avatarBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 17, letterSpacing: 1,
          boxShadow: `0 3px 10px ${role.avatarBg}55`,
          overflow: 'hidden',
        }}>
          {u.brand_logo
            ? <img src={u.brand_logo} alt={u.username} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : initials}
        </div>

        {/* Name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {u.username}
          </div>
          {u.phone ? (
            <a href={`tel:${u.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, color: '#16A34A', fontSize: 12, textDecoration: 'none' }}>
              <IconPhone /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.phone}</span>
            </a>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, color: '#ccc', fontSize: 12 }}>
              <IconPhone /><span>—</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1, color: '#bbb', fontSize: 11 }}>
            <IconCal /><span>Joined {new Date(u.created_at).toLocaleDateString('en', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
          </div>
        </div>

        {/* Role badge */}
        <div style={{
          background: role.bg, color: role.color, border: `1px solid ${role.border}`,
          borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
          letterSpacing: 0.5, flexShrink: 0,
        }}>
          {role.label}
        </div>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'flex', borderTop: '1px solid #F5F5F5', borderBottom: '1px solid #F5F5F5' }}>
        {/* Brands stat */}
        <div style={{ flex: 1, borderRight: '1px solid #F5F5F5', display: 'flex', position: 'relative' }}>
          <button
            onClick={onGoBrands}
            title="View Brands"
            style={{
              flex: 1, padding: '7px 8px', border: 'none', background: 'none',
              cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#F0F7FF'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <div style={{ color: '#2563EB', display: 'flex' }}><IconBrands /></div>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{u.brand_count || 0}</span>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>BRANDS</span>
          </button>
          <button
            onClick={e => { e.stopPropagation(); onAddBrand(); }}
            title="Add Brand"
            style={{
              position: 'absolute', top: 5, right: 5,
              width: 18, height: 18, borderRadius: 5, border: 'none',
              background: '#EF4444', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, lineHeight: 1, padding: 0,
              transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#B91C1C'}
            onMouseOut={e => e.currentTarget.style.background = '#EF4444'}
          >＋</button>
        </div>

        {/* Branches stat */}
        <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
          <button
            onClick={onGoBranches}
            title="View Branches"
            style={{
              flex: 1, padding: '7px 8px', border: 'none', background: 'none',
              cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#F0FDF4'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <div style={{ color: '#16A34A', display: 'flex' }}><IconBranches /></div>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{u.branch_count || 0}</span>
            <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>BRANCHES</span>
          </button>
          <button
            onClick={e => { e.stopPropagation(); onAddBranch(); }}
            title="Add Branch"
            style={{
              position: 'absolute', top: 5, right: 5,
              width: 18, height: 18, borderRadius: 5, border: 'none',
              background: '#EF4444', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, lineHeight: 1, padding: 0,
              transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#B91C1C'}
            onMouseOut={e => e.currentTarget.style.background = '#EF4444'}
          >＋</button>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px' }}>
        <button
          onClick={onEdit}
          style={{
            flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #E0E0E0',
            background: '#FAFAFA', color: '#444', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#E3F2FD'; e.currentTarget.style.borderColor = '#90CAF9'; e.currentTarget.style.color = '#1565C0'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#FAFAFA'; e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.color = '#444'; }}
        >
          <IconEdit /> Edit
        </button>
        <button
          onClick={onDelete}
          style={{
            flex: 1, height: 30, borderRadius: 8, border: '1.5px solid #FFCDD2',
            background: '#FFF5F5', color: '#C62828', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = '#FFEBEE'; e.currentTarget.style.borderColor = '#EF9A9A'; }}
          onMouseOut={e => { e.currentTarget.style.background = '#FFF5F5'; e.currentTarget.style.borderColor = '#FFCDD2'; }}
        >
          <IconTrash /> Delete
        </button>
      </div>
    </div>
  );
}

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', phone: '', role: 'owner' });

  const load = () => {
    setLoading(true);
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm({ username: '', password: '', phone: '', role: 'owner' }); setShowModal(true); };
  const openEdit = (u) => { setEditing(u); setForm({ username: u.username, password: '', phone: u.phone || '', role: u.role }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { phone: form.phone, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put('/users/' + editing.id, payload);
        toast.success('User updated!');
      } else {
        await api.post('/users', form);
        toast.success('User created!');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"? This will also delete all their brands and menus!`)) return;
    try {
      await api.delete('/users/' + id);
      toast.success('User deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  const admins = users.filter(u => u.role === 'admin');
  const owners = users.filter(u => u.role === 'owner');

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>👥</span> User Management
          </h1>
          <p style={{ color: '#757575', fontSize: 13, marginTop: 4 }}>
            {admins.length} admin{admins.length !== 1 ? 's' : ''} · {owners.length} owner{owners.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>＋</span> Add User
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>No users yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Click "+ Add User" to create your first user</div>
        </div>
      ) : (
        <>
          {/* Admins section */}
          {admins.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', letterSpacing: 1, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: '2px 8px' }}>ADMINS</span>
                <span style={{ color: '#aaa', fontWeight: 400 }}>{admins.length}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {admins.map(u => (
                  <UserCard key={u.id} u={u}
                    onEdit={() => openEdit(u)}
                    onDelete={() => handleDelete(u.id, u.username)}
                    onGoBrands={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username } })}
                    onGoBranches={() => navigate(`/users/${u.id}/branches`, { state: { username: u.username } })}
                    onAddBrand={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username, openAdd: true } })}
                    onAddBranch={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username, openAddBranch: true } })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Owners section */}
          {owners.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', letterSpacing: 1, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '2px 8px' }}>OWNERS</span>
                <span style={{ color: '#aaa', fontWeight: 400 }}>{owners.length}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {owners.map(u => (
                  <UserCard key={u.id} u={u}
                    onEdit={() => openEdit(u)}
                    onDelete={() => handleDelete(u.id, u.username)}
                    onGoBrands={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username } })}
                    onGoBranches={() => navigate(`/users/${u.id}/branches`, { state: { username: u.username } })}
                    onAddBrand={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username, openAdd: true } })}
                    onAddBranch={() => navigate('/', { state: { filterUser: u.id, filterUsername: u.username, openAddBranch: true } })}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: form.role === 'admin' ? '#2563EB' : '#16A34A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <IconUser />
                </div>
                <span className="modal-title">{editing ? `Edit — ${editing.username}` : 'Add New User'}</span>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit} autoComplete="off">
              {!editing && (
                <div className="input-group">
                  <label>Username</label>
                  <input className="input" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required placeholder="e.g. cairo_grill" autoComplete="off" />
                </div>
              )}
              <div className="input-group">
                <label>Phone</label>
                <input className="input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+20 10 1234 5678" autoComplete="off" />
              </div>
              <div className="input-group">
                <label>{editing ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input className="input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" required={!editing} autoComplete="new-password" />
              </div>
              <div className="input-group">
                <label>Role</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['owner', 'admin'].map(r => (
                    <button key={r} type="button"
                      onClick={() => setForm({...form, role: r})}
                      style={{
                        flex: 1, padding: '10px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                        border: `2px solid ${form.role === r ? (r === 'admin' ? '#3B82F6' : '#22C55E') : '#E0E0E0'}`,
                        background: form.role === r ? (r === 'admin' ? '#EFF6FF' : '#F0FDF4') : '#fff',
                        color: form.role === r ? (r === 'admin' ? '#1D4ED8' : '#15803D') : '#555',
                        transition: 'all 0.15s',
                      }}>
                      {r === 'admin' ? '🛡 Admin' : '👤 Owner'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{
                background: form.role === 'admin' ? '#EFF6FF' : '#F0FDF4',
                border: `1px solid ${form.role === 'admin' ? '#BFDBFE' : '#BBF7D0'}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 13,
                color: form.role === 'admin' ? '#1E40AF' : '#166534',
                marginBottom: 16,
              }}>
                {form.role === 'admin'
                  ? '🛡 Admin can manage all users, brands and menus.'
                  : '👤 Owner can only manage their own brands and menus.'}
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                {editing ? 'Save Changes' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
